import type { ApiClient } from './client.ts';
import type { components } from './schema.d.ts';

/**
 * Named, typed calls onto the generated schema.
 *
 * Every request and response type below is `components['schemas'][…]` — read straight from the
 * OpenAPI document the API emits. Nothing here restates a shape by hand, so a field renamed on
 * the server breaks the build here instead of silently arriving as `undefined` in a browser.
 *
 * The helpers exist because a bare `client.get<T>('/api/v1/…')` puts the path and the type in
 * different places, and the two drift. Here they sit on one line.
 */
type Schema = components['schemas'];

export type Locale = 'ar' | 'en';

/* ── catalogue ───────────────────────────────────────────────────────────── */

export interface ProductQueryParams {
  locale?: Locale;
  search?: string;
  category?: string;
  minPrice?: number;
  maxPrice?: number;
  optionValueIds?: number[];
  inStock?: boolean;
  featured?: boolean;
  sort?: 'newest' | 'price-asc' | 'price-desc' | 'rating' | 'name';
  page?: number;
  pageSize?: number;
}

export function catalogApi(client: ApiClient) {
  return {
    categories: (locale: Locale) =>
      client.get<Schema['CategoryTreeNode'][]>('/api/v1/catalog/categories', { query: { locale } }),

    products: (params: ProductQueryParams) =>
      client.get<Schema['PagedResultOfProductListItemResponse']>('/api/v1/catalog/products', {
        query: { ...params },
      }),

    product: (slug: string, locale: Locale) =>
      client.get<Schema['ProductDetailResponse']>(
        // Encoded, because Arabic slugs are the normal case here, not the exception.
        `/api/v1/catalog/products/${encodeURIComponent(slug)}`,
        { query: { locale } },
      ),

    facets: (locale: Locale, category?: string) =>
      client.get<Schema['ProductFacetsResponse']>('/api/v1/catalog/facets', {
        query: { locale, category },
      }),

    reviews: (productId: number, page?: number, pageSize?: number) =>
      client.get<Schema['ProductReviewsResponse']>(
        `/api/v1/catalog/products/${productId}/reviews`,
        { query: { page, pageSize } },
      ),
  };
}

/* ── basket ──────────────────────────────────────────────────────────────── */

export function cartApi(client: ApiClient) {
  return {
    get: (locale: Locale, coupon?: string) =>
      client.get<Schema['CartResponse']>('/api/v1/cart', { query: { locale, coupon } }),

    addItem: (body: Schema['AddCartItemRequest'], locale: Locale) =>
      client.post<Schema['CartResponse']>('/api/v1/cart/items', { body, query: { locale } }),

    updateQuantity: (itemId: number, body: Schema['UpdateCartItemRequest'], locale: Locale) =>
      client.put<Schema['CartResponse']>(`/api/v1/cart/items/${itemId}`, { body, query: { locale } }),

    removeItem: (itemId: number, locale: Locale) =>
      client.delete<Schema['CartResponse']>(`/api/v1/cart/items/${itemId}`, { query: { locale } }),

    clear: () => client.delete<void>('/api/v1/cart'),

    applyCoupon: (body: Schema['ApplyCouponRequest'], locale: Locale) =>
      client.post<Schema['CartResponse']>('/api/v1/cart/coupon', { body, query: { locale } }),
  };
}

/* ── checkout ────────────────────────────────────────────────────────────── */

export function checkoutApi(client: ApiClient) {
  return {
    placeOrder: (body: Schema['CheckoutRequest']) =>
      client.post<Schema['CheckoutResponse']>('/api/v1/checkout', { body }),

    /**
     * Settles the order's payment. Only its owner may: a signed-in buyer needs nothing more, a
     * guest quotes the tracking token returned when the order was placed. Anyone else gets a 404.
     */
    confirm: (orderNumber: string, trackingToken?: string) =>
      client.post<Schema['OrderResponse']>(`/api/v1/checkout/${orderNumber}/confirm`, {
        body: { trackingToken: trackingToken ?? null },
      }),

    /** A guest reads their order by quoting the email it was placed with. */
    order: (orderNumber: string, email?: string) =>
      client.get<Schema['OrderResponse']>(`/api/v1/checkout/${orderNumber}`, { query: { email } }),
  };
}

/**
 * Following an order without an account.
 *
 * The token goes in the path rather than a query string on purpose. Query strings are the part
 * of a URL that ends up in access logs and analytics by default, and this one is a credential.
 */
export function orderTrackingApi(client: ApiClient) {
  return {
    track: (token: string) =>
      client.get<Schema['OrderResponse']>(
        `/api/v1/orders/track/${encodeURIComponent(token)}`,
        // A bad token is the answer, not a session problem — there is nothing to refresh.
        { skipRefresh: true },
      ),
  };
}

/* ── account ─────────────────────────────────────────────────────────────── */

export function authApi(client: ApiClient) {
  return {
    // skipRefresh on the auth endpoints themselves: a failed sign-in is a 401 that means "wrong
    // password", and trying to refresh through it would be nonsense.
    register: (body: Schema['RegisterRequest']) =>
      client.post<void>('/api/v1/auth/register', { body, skipRefresh: true }),

    login: (body: Schema['LoginRequest']) =>
      client.post<Schema['UserProfileResponse']>('/api/v1/auth/login', { body, skipRefresh: true }),

    google: (body: Schema['GoogleSignInRequest']) =>
      client.post<Schema['UserProfileResponse']>('/api/v1/auth/google', { body, skipRefresh: true }),

    logout: () => client.post<void>('/api/v1/auth/logout', { skipRefresh: true }),

    confirmEmail: (body: Schema['ConfirmEmailRequest']) =>
      client.post<void>('/api/v1/auth/confirm-email', { body, skipRefresh: true }),

    forgotPassword: (body: Schema['ForgotPasswordRequest']) =>
      client.post<void>('/api/v1/auth/forgot-password', { body, skipRefresh: true }),

    resendConfirmation: (body: Schema['ResendConfirmationRequest']) =>
      client.post<void>('/api/v1/auth/resend-confirmation', { body, skipRefresh: true }),

    resetPassword: (body: Schema['ResetPasswordRequest']) =>
      client.post<void>('/api/v1/auth/reset-password', { body, skipRefresh: true }),
  };
}

export function accountApi(client: ApiClient) {
  return {
    /**
     * The signed-in customer's profile.
     *
     * Takes options because callers use this two different ways. Reading a profile to show it
     * wants the ordinary behaviour: a 401 means the session lapsed, so refresh and retry. Asking
     * "is anyone signed in?" does not — a 401 *is* the answer, and attempting a refresh for a
     * visitor who was never signed in is a wasted round trip whose failure then looks like an
     * expired session. Those callers pass `skipRefresh`.
     */
    me: (options?: { skipRefresh?: boolean }) =>
      client.get<Schema['UserProfileResponse']>('/api/v1/account/me', options),

    updateProfile: (body: Schema['UpdateProfileRequest']) =>
      client.put<Schema['UserProfileResponse']>('/api/v1/account/me', { body }),

    changePassword: (body: Schema['ChangePasswordRequest']) =>
      client.post<void>('/api/v1/account/change-password', { body }),

    addresses: () => client.get<Schema['AddressResponse'][]>('/api/v1/account/addresses'),

    createAddress: (body: Schema['SaveAddressRequest']) =>
      client.post<Schema['AddressResponse']>('/api/v1/account/addresses', { body }),

    updateAddress: (id: number, body: Schema['SaveAddressRequest']) =>
      client.put<Schema['AddressResponse']>(`/api/v1/account/addresses/${id}`, { body }),

    deleteAddress: (id: number) => client.delete<void>(`/api/v1/account/addresses/${id}`),

    orders: (page?: number, pageSize?: number) =>
      client.get<Schema['PagedResultOfOrderListItemResponse']>('/api/v1/account/orders', {
        query: { page, pageSize },
      }),

    order: (number: string) =>
      client.get<Schema['OrderDetailResponse']>(`/api/v1/account/orders/${number}`),

    cancelOrder: (number: string, body: Schema['CancelOrderRequest']) =>
      client.post<Schema['OrderDetailResponse']>(`/api/v1/account/orders/${number}/cancel`, { body }),

    reviewableProducts: (locale: Locale) =>
      client.get<Schema['ReviewableProductResponse'][]>('/api/v1/account/reviews/pending', {
        query: { locale },
      }),

    submitReview: (body: Schema['SubmitReviewRequest']) =>
      client.post<Schema['ReviewResponse']>('/api/v1/account/reviews', { body }),
  };
}

/* ── content and settings ────────────────────────────────────────────────── */

export function contentApi(client: ApiClient) {
  return {
    page: (page: string, locale: Locale) =>
      client.get<Record<string, Schema['ContentBlockResponse']>>(
        `/api/v1/content/pages/${encodeURIComponent(page)}`,
        { query: { locale } },
      ),

    announcements: (locale: Locale) =>
      client.get<Schema['AnnouncementResponse'][]>('/api/v1/content/announcements', {
        query: { locale },
      }),

    faq: (locale: Locale) =>
      client.get<Schema['FaqItemResponse'][]>('/api/v1/content/faq', { query: { locale } }),

    publicSettings: () => client.get<Schema['PublicSettingsResponse']>('/api/v1/settings/public'),

    submitInquiry: (body: Schema['SubmitInquiryRequest']) =>
      client.post<void>('/api/v1/inquiries', { body }),

    emirates: () => client.get<string[]>('/api/v1/reference/emirates'),
  };
}

/* ── back office ─────────────────────────────────────────────────────────── */

/**
 * The admin endpoints.
 *
 * Deliberately not part of `createApi`. That function calls every group constructor it knows
 * about, so anything added there is retained in whatever bundle imports it — and the storefront
 * has no business shipping the paths to the back office. The admin imports this instead.
 */
export function createAdminApi(client: ApiClient) {
  return {
    client,

    dashboard: () => client.get<Schema['OrderDashboardResponse']>('/api/v1/admin/dashboard'),

    products: (query: Record<string, string | number | undefined> = {}) =>
      client.get<Schema['PagedResultOfAdminProductListItemResponse']>('/api/v1/admin/products', {
        query,
      }),

    reviews: (query: Record<string, string | number | undefined> = {}) =>
      client.get<Schema['PagedResultOfAdminReviewResponse']>('/api/v1/admin/reviews', { query }),

    /** Counts keyed by status name — `New`, `InProgress`, `Resolved`, `Spam`. */
    inquiryCounts: () => client.get<Record<string, number>>('/api/v1/admin/inquiries/counts'),

    orders: (query: Record<string, string | number | undefined> = {}) =>
      client.get<Schema['PagedResultOfOrderListItemResponse']>('/api/v1/admin/orders', { query }),

    /* ── catalogue ──────────────────────────────────────────────────────── */

    product: (id: number) =>
      client.get<Schema['AdminProductResponse']>(`/api/v1/admin/products/${id}`),

    createProduct: (body: Schema['SaveProductRequest']) =>
      client.post<Schema['AdminProductResponse']>('/api/v1/admin/products', { body }),

    updateProduct: (id: number, body: Schema['SaveProductRequest']) =>
      client.put<Schema['AdminProductResponse']>(`/api/v1/admin/products/${id}`, { body }),

    deleteProduct: (id: number) => client.delete<void>(`/api/v1/admin/products/${id}`),

    /**
     * Archiving takes a product off the shop without deleting it.
     *
     * The distinction matters: an archived product keeps its history, so past orders still name
     * what was bought. Deletion is only possible while nothing references it.
     */
    archiveProduct: (id: number) =>
      client.post<Schema['AdminProductResponse']>(`/api/v1/admin/products/${id}/archive`),

    restoreProduct: (id: number) =>
      client.post<Schema['AdminProductResponse']>(`/api/v1/admin/products/${id}/restore`),

    uploadProductImage: (id: number, file: File) =>
      client.upload<Schema['AdminProductImageResponse']>(`/api/v1/admin/products/${id}/images`, file),

    updateProductImage: (
      id: number,
      imageId: number,
      body: Schema['UpdateImageMetadataRequest'],
    ) => client.put<Schema['AdminProductImageResponse']>(
      `/api/v1/admin/products/${id}/images/${imageId}`,
      { body },
    ),

    deleteProductImage: (id: number, imageId: number) =>
      client.delete<void>(`/api/v1/admin/products/${id}/images/${imageId}`),

    reorderProductImages: (id: number, orderedIds: number[]) =>
      client.post<void>(`/api/v1/admin/products/${id}/images/reorder`, { body: { orderedIds } }),

    /* ── categories ─────────────────────────────────────────────────────── */

    categories: () => client.get<Schema['AdminCategoryResponse'][]>('/api/v1/admin/categories'),

    category: (id: number) =>
      client.get<Schema['AdminCategoryResponse']>(`/api/v1/admin/categories/${id}`),

    createCategory: (body: Schema['SaveCategoryRequest']) =>
      client.post<Schema['AdminCategoryResponse']>('/api/v1/admin/categories', { body }),

    updateCategory: (id: number, body: Schema['SaveCategoryRequest']) =>
      client.put<Schema['AdminCategoryResponse']>(`/api/v1/admin/categories/${id}`, { body }),

    deleteCategory: (id: number) => client.delete<void>(`/api/v1/admin/categories/${id}`),

    /* ── discount codes ──────────────────────────────────────────────────── */

    coupons: () => client.get<Schema['CouponResponse'][]>('/api/v1/admin/coupons'),

    createCoupon: (body: Schema['SaveCouponRequest']) =>
      client.post<Schema['CouponResponse']>('/api/v1/admin/coupons', { body }),

    updateCoupon: (id: number, body: Schema['SaveCouponRequest']) =>
      client.put<Schema['CouponResponse']>(`/api/v1/admin/coupons/${id}`, { body }),

    deleteCoupon: (id: number) => client.delete<void>(`/api/v1/admin/coupons/${id}`),

    reorderCategories: (orderedIds: number[]) =>
      client.post<void>('/api/v1/admin/categories/reorder', { body: { orderedIds } }),

    /* ── orders ─────────────────────────────────────────────────────────── */

    /** Orders are addressed by their human number (`AWJ-…`), which is what staff read out. */
    order: (number: string, locale?: string) =>
      client.get<Schema['OrderDetailResponse']>(`/api/v1/admin/orders/${encodeURIComponent(number)}`, {
        query: { locale },
      }),

    /**
     * The statuses this order may move to next.
     *
     * Asked of the server rather than derived in the browser: the state machine lives in the
     * domain, and a second copy here would drift the first time a rule changed.
     */
    orderTransitions: (number: string) =>
      client.get<string[]>(`/api/v1/admin/orders/${encodeURIComponent(number)}/transitions`),

    markOrderRefunded: (number: string, body: Schema['MarkRefundedRequest'], locale?: string) =>
      client.post<Schema['OrderDetailResponse']>(
        `/api/v1/admin/orders/${encodeURIComponent(number)}/refunded`,
        { body, query: { locale } },
      ),

    transitionOrder: (number: string, body: Schema['TransitionOrderRequest'], locale?: string) =>
      client.post<Schema['OrderDetailResponse']>(
        `/api/v1/admin/orders/${encodeURIComponent(number)}/transition`,
        { body, query: { locale } },
      ),

    /* ── customers ──────────────────────────────────────────────────────── */

    customers: (query: Record<string, string | number | boolean | undefined> = {}) =>
      client.get<Schema['PagedResultOfCustomerListItemResponse']>('/api/v1/admin/customers', { query }),

    customer: (id: number) =>
      client.get<Schema['CustomerDetailResponse']>(`/api/v1/admin/customers/${id}`),

    blockCustomer: (id: number, body: Schema['BlockCustomerRequest']) =>
      client.post<Schema['CustomerDetailResponse']>(`/api/v1/admin/customers/${id}/block`, { body }),

    unblockCustomer: (id: number) =>
      client.post<Schema['CustomerDetailResponse']>(`/api/v1/admin/customers/${id}/unblock`),

    resendCustomerConfirmation: (id: number) =>
      client.post<void>(`/api/v1/admin/customers/${id}/resend-confirmation`),

    deleteCustomer: (id: number) => client.delete<void>(`/api/v1/admin/customers/${id}`),

    /* ── reviews ────────────────────────────────────────────────────────── */

    moderateReview: (id: number, body: Schema['ModerateReviewRequest']) =>
      client.post<Schema['AdminReviewResponse']>(`/api/v1/admin/reviews/${id}/moderate`, { body }),

    replyToReview: (id: number, body: Schema['ReplyToReviewRequest']) =>
      client.post<Schema['AdminReviewResponse']>(`/api/v1/admin/reviews/${id}/reply`, { body }),

    /* ── enquiries ──────────────────────────────────────────────────────── */

    inquiries: (query: Record<string, string | number | undefined> = {}) =>
      client.get<Schema['PagedResultOfInquiryListItemResponse']>('/api/v1/admin/inquiries', { query }),

    inquiry: (id: number) => client.get<Schema['InquiryResponse']>(`/api/v1/admin/inquiries/${id}`),

    setInquiryStatus: (id: number, body: Schema['UpdateInquiryStatusRequest']) =>
      client.post<Schema['InquiryResponse']>(`/api/v1/admin/inquiries/${id}/status`, { body }),

    addInquiryNote: (id: number, body: Schema['AddInquiryNoteRequest']) =>
      client.post<Schema['InquiryResponse']>(`/api/v1/admin/inquiries/${id}/notes`, { body }),

    /* ── content ────────────────────────────────────────────────────────── */

    contentBlocks: (page?: string) =>
      client.get<Schema['AdminContentBlockResponse'][]>('/api/v1/admin/content/blocks', {
        query: { page },
      }),

    saveContentBlock: (id: number | null, body: Schema['SaveContentBlockRequest']) =>
      id === null
        ? client.post<Schema['AdminContentBlockResponse']>('/api/v1/admin/content/blocks', { body })
        : client.put<Schema['AdminContentBlockResponse']>(`/api/v1/admin/content/blocks/${id}`, { body }),

    deleteContentBlock: (id: number) => client.delete<void>(`/api/v1/admin/content/blocks/${id}`),

    announcements: () =>
      client.get<Schema['AdminAnnouncementResponse'][]>('/api/v1/admin/content/announcements'),

    saveAnnouncement: (id: number | null, body: Schema['SaveAnnouncementRequest']) =>
      id === null
        ? client.post<Schema['AdminAnnouncementResponse']>('/api/v1/admin/content/announcements', { body })
        : client.put<Schema['AdminAnnouncementResponse']>(`/api/v1/admin/content/announcements/${id}`, { body }),

    deleteAnnouncement: (id: number) => client.delete<void>(`/api/v1/admin/content/announcements/${id}`),

    faq: () => client.get<Schema['AdminFaqItemResponse'][]>('/api/v1/admin/content/faq'),

    saveFaqItem: (id: number | null, body: Schema['SaveFaqItemRequest']) =>
      id === null
        ? client.post<Schema['AdminFaqItemResponse']>('/api/v1/admin/content/faq', { body })
        : client.put<Schema['AdminFaqItemResponse']>(`/api/v1/admin/content/faq/${id}`, { body }),

    deleteFaqItem: (id: number) => client.delete<void>(`/api/v1/admin/content/faq/${id}`),

    media: () => client.get<Schema['MediaAssetResponse'][]>('/api/v1/admin/content/media'),

    uploadMedia: (file: File) =>
      client.upload<Schema['MediaAssetResponse']>('/api/v1/admin/content/media', file),

    updateMediaAlt: (id: number, body: Schema['UpdateMediaAltRequest']) =>
      client.put<Schema['MediaAssetResponse']>(`/api/v1/admin/content/media/${id}`, { body }),

    deleteMedia: (id: number) => client.delete<void>(`/api/v1/admin/content/media/${id}`),

    /* ── settings, staff and the audit trail — SuperAdmin only ──────────── */

    settings: () => client.get<Schema['SettingGroupResponse'][]>('/api/v1/admin/settings'),

    saveSettings: (group: string, values: Record<string, string | null>) =>
      client.put<Schema['SettingGroupResponse']>(`/api/v1/admin/settings/${encodeURIComponent(group)}`, {
        body: { values },
      }),

    staff: () => client.get<Schema['StaffMemberResponse'][]>('/api/v1/admin/staff'),

    inviteStaff: (body: Schema['InviteStaffRequest']) =>
      client.post<Schema['StaffMemberResponse']>('/api/v1/admin/staff', { body }),

    changeStaffRole: (id: number, role: string) =>
      client.post<Schema['StaffMemberResponse']>(`/api/v1/admin/staff/${id}/role`, { body: { role } }),

    setStaffBlocked: (id: number, blocked: boolean) =>
      client.post<Schema['StaffMemberResponse']>(
        `/api/v1/admin/staff/${id}/${blocked ? 'block' : 'unblock'}`,
      ),

    audit: (query: Record<string, string | number | undefined> = {}) =>
      client.get<Schema['PagedResultOfAuditEntryResponse']>('/api/v1/admin/audit', { query }),

    /* ── exports ────────────────────────────────────────────────────────── */

    /** A table as a PDF. The panel sends it already in words; the answer is the file itself. */
    exportTablePdf: (body: Schema['ExportTableRequest']) =>
      client.post<Blob>('/api/v1/admin/exports/pdf', { body, responseType: 'blob' }),
  };
}

export type AwjAdminApi = ReturnType<typeof createAdminApi>;

/**
 * Everything the storefront needs, bound to one client.
 */
export function createApi(client: ApiClient) {
  return {
    client,
    catalog: catalogApi(client),
    cart: cartApi(client),
    checkout: checkoutApi(client),
    orders: orderTrackingApi(client),
    auth: authApi(client),
    account: accountApi(client),
    content: contentApi(client),
  };
}

export type AwjApi = ReturnType<typeof createApi>;
